import { useEffect, useMemo } from 'react';
import {
  DropDown,
  Drawer,
  TextField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
  Loading,
} from '@elementary/common-ui/components';
import {
  InlineFormContent,
  SectionTitle,
} from '@elementary/common-ui/components/FormControls';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { ROLE_ID, NEW_VALUE } from '@elementary/common-ui/constants/urlParams';
import styled from 'styled-components';
import useEnvironments from 'shared/hooks/useEnvironments';
import { useRole } from '../hooks/useRole';
import { useRoles } from '../../shared/useRoles';
import {
  AccountLevelPermission,
  PermissionsAccess,
  ResourceFilterProperty,
  RoleFormData,
} from '../../shared/types';
import EnvironmentsPermissionList from './EnvironmentsPermissionList';
import PermissionsAccessDropdown from './PermissionsAccessDropdown';

const ACCOUNT_PERMISSION_OPTIONS = [
  {
    value: AccountLevelPermission.NO_PERMISSIONS,
    label: 'No permission',
    description:
      'Cannot invite or assign roles to others, and cannot manage environments',
  },
  {
    value: AccountLevelPermission.FULL_MANAGEMENT,
    label: 'Full account management',
    description: 'Can invite users, assign roles, and manage environments',
  },
];

type Props = {
  onClose: () => void;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Section = styled(FlexColumn)`
  gap: 16px;
`;

const RoleDrawer = ({ onClose }: Props) => {
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get(ROLE_ID);

  const isOpen = !!roleId;
  const isCreateMode = roleId === NEW_VALUE;
  const isEditMode = !isCreateMode && !!roleId && roleId !== NEW_VALUE;

  const { data: environmentsData, isLoading: isEnvironmentsLoading } =
    useEnvironments();
  const { role, isLoading: isRoleLoading } = useRole(roleId || '', isEditMode);
  const { createRoleAsync, updateRoleAsync, isCreating, isUpdating } =
    useRoles();

  const isViewOnly = !!role?.builtin;
  const isFormLoading = isRoleLoading || isEnvironmentsLoading;

  // Stable key so polling refetches that keep the same env set do not reset the form.
  const environmentIdsKey = useMemo(
    () => (environmentsData || []).map((env) => env.id).join(','),
    [environmentsData],
  );

  const defaultValues: Partial<RoleFormData> = useMemo(() => {
    const defaultAccess =
      role?.new_environment_default_access || PermissionsAccess.HIDDEN;

    const permissionByEnvId = new Map(
      (role?.env_permissions || []).map((envPermission) => [
        envPermission.environment_id,
        envPermission,
      ]),
    );

    const toFormValue = (environmentId: string) => {
      const envPermission = permissionByEnvId.get(environmentId);
      return {
        environmentId,
        featuresAccess: envPermission?.features_access ?? defaultAccess,
        customFeaturesAccess: envPermission?.custom_features_access || null,
        defaultResourcePermission:
          envPermission?.default_resource_permission ?? defaultAccess,
        resourceFilterGroups:
          role?.resource_group_permissions
            ?.filter((rg) => rg.environment_id === environmentId)
            .map((rg) => ({
              access: rg.access,
              filters: rg.resource_filters.map((filter) => ({
                property: filter.property,
                operator: filter.operator,
                value: filter.value,
              })),
            })) || [],
      };
    };

    const visibleEnvIds = new Set(
      (environmentsData || []).map((env) => env.id),
    );
    const visibleEnvPermissions = (environmentsData || []).map((env) =>
      toFormValue(env.id),
    );
    const hiddenEnvPermissions = (role?.env_permissions || [])
      .filter(
        (envPermission) => !visibleEnvIds.has(envPermission.environment_id),
      )
      .map((envPermission) => toFormValue(envPermission.environment_id));

    return {
      name: role?.name || '',
      description: role?.description || '',
      accountLevelPermission:
        role?.account_level_permission || AccountLevelPermission.NO_PERMISSIONS,
      newEnvironmentDefaultAccess: defaultAccess,
      envPermissions: [...visibleEnvPermissions, ...hiddenEnvPermissions],
    };
    // environmentIdsKey stands in for environmentsData identity so polling
    // with an unchanged env set does not rebuild defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, environmentIdsKey]);

  const methods = useForm<RoleFormData>({
    defaultValues,
    mode: 'onChange',
  });

  const { isValid, isDirty } = methods.formState;

  useEffect(() => {
    if (isOpen && !isFormLoading) {
      methods.reset?.(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isFormLoading, role, environmentIdsKey]);

  const onSubmit = async (data: RoleFormData) => {
    try {
      const env_permissions = data.envPermissions.map((env) => ({
        environment_id: env.environmentId,
        features_access:
          env.featuresAccess === PermissionsAccess.CUSTOM
            ? PermissionsAccess.HIDDEN
            : env.featuresAccess,
        custom_features_access: env.customFeaturesAccess,
        default_resource_permission: env.defaultResourcePermission,
      }));

      const resource_group_permissions = data.envPermissions.flatMap((env) =>
        (env.resourceFilterGroups || [])
          .filter((group) => group.filters.length > 0)
          .map((group) => ({
            environment_id: env.environmentId,
            access: group.access,
            resource_filters: group.filters.map((filter) => ({
              property: filter.property as ResourceFilterProperty,
              operator: filter.operator,
              value: filter.value,
            })),
          })),
      );

      if (isCreateMode) {
        await createRoleAsync({
          name: data.name,
          description: data.description || undefined,
          account_level_permission: data.accountLevelPermission,
          new_environment_default_access: data.newEnvironmentDefaultAccess,
          env_permissions,
          resource_group_permissions,
        });
      } else if (isEditMode && roleId) {
        await updateRoleAsync({
          roleId,
          data: {
            name: data.name,
            description: data.description || undefined,
            account_level_permission: data.accountLevelPermission,
            new_environment_default_access: data.newEnvironmentDefaultAccess,
            env_permissions,
            resource_group_permissions,
          },
        });
      }
      onClose();
    } catch (error) {
      // Error handling is done in the useRoles hook
    }
  };

  const isSubmitDisabled = useMemo(
    () =>
      !isValid ||
      isFormLoading ||
      role?.builtin ||
      (isEditMode && !isDirty) ||
      isViewOnly,
    [isValid, isFormLoading, role?.builtin, isEditMode, isDirty, isViewOnly],
  );

  const title = useMemo(() => {
    if (isViewOnly) {
      return 'View role';
    }
    return isEditMode ? 'Edit role' : 'Create role';
  }, [isEditMode, isViewOnly]);

  return (
    <Drawer
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      submitButtonLabel="Save"
      onSubmit={methods.handleSubmit(onSubmit)}
      submitDisabled={isSubmitDisabled}
      isLoading={isCreating || isUpdating}
    >
      {isFormLoading ? (
        <Loading />
      ) : (
        <FormProvider {...methods}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Section>
              <TextField
                {...methods.register('name', {
                  required: requiredLabel,
                })}
                fullWidth
                size="large"
                label="Name"
                placeholder="e.g. Custom role"
                disabled={isViewOnly}
              />

              <TextField
                {...methods.register('description')}
                fullWidth
                size="large"
                label="Description"
                placeholder="Enter role description"
                disabled={isViewOnly}
              />
            </Section>

            <Section>
              <SectionTitle>Account management permissions</SectionTitle>

              <Controller
                name="accountLevelPermission"
                control={methods.control}
                render={({ field }) => (
                  <DropDown
                    size="large"
                    options={ACCOUNT_PERMISSION_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isViewOnly}
                  />
                )}
              />
            </Section>

            <Section>
              <SectionTitle>Environment permissions</SectionTitle>

              <InlineFormContent>
                When a new environment is created it is by default
                <span>
                  <Controller
                    name="newEnvironmentDefaultAccess"
                    control={methods.control}
                    render={({ field }) => (
                      <PermissionsAccessDropdown
                        value={field.value}
                        disabled={isViewOnly}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </span>
              </InlineFormContent>

              <EnvironmentsPermissionList disabled={isViewOnly} />
            </Section>
          </Form>
        </FormProvider>
      )}
    </Drawer>
  );
};

export default RoleDrawer;
